import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { SUPPORTED_LANGUAGE_CODES, isRtlLanguage } from './languages';
import en from './locales/en.json';

/**
 * English ships in the initial bundle so the first paint always has
 * translations ready. Every other language is a dynamic import so users
 * only download the dictionary they actually use.
 */
const LANG_LOADERS: Record<string, () => Promise<{ default: Record<string, unknown> }>> = {
  ja: () => import('./locales/ja.json'),
  ko: () => import('./locales/ko.json'),
  zh: () => import('./locales/zh.json'),
  de: () => import('./locales/de.json'),
  fr: () => import('./locales/fr.json'),
  es: () => import('./locales/es.json'),
  pt: () => import('./locales/pt.json'),
  ar: () => import('./locales/ar.json'),
  hi: () => import('./locales/hi.json'),
  ru: () => import('./locales/ru.json'),
  id: () => import('./locales/id.json'),
  bn: () => import('./locales/bn.json'),
  ur: () => import('./locales/ur.json'),
};

const loaded = new Set<string>(['en']);

async function ensureLanguageLoaded(lng: string) {
  if (loaded.has(lng)) return;
  const loader = LANG_LOADERS[lng];
  if (!loader) return;
  try {
    const mod = await loader();
    i18n.addResourceBundle(lng, 'translation', mod.default, true, true);
    loaded.add(lng);
  } catch {
    // Network or chunk failure → silently fall back to English.
  }
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en } },
    partialBundledLanguages: true,
    supportedLngs: SUPPORTED_LANGUAGE_CODES,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'knowlez.lang',
    },
  });

const initialLng = i18n.language?.split('-')[0] || 'en';
if (initialLng !== 'en') {
  void ensureLanguageLoaded(initialLng);
}

i18n.on('languageChanged', (lng) => {
  const base = lng?.split('-')[0] || 'en';
  void ensureLanguageLoaded(base);
});

function applyHtmlLangDir(lng: string) {
  document.documentElement.lang = lng;
  document.documentElement.dir = isRtlLanguage(lng) ? 'rtl' : 'ltr';
}

applyHtmlLangDir(i18n.language || 'en');
i18n.on('languageChanged', applyHtmlLangDir);

// Fire-and-forget: on first visit, ask the backend's geo hint
// (Cloudflare cf-ipcountry) and switch language + currency accordingly.
void import('./geo').then(({ applyGeoOnFirstVisit }) => {
  void applyGeoOnFirstVisit();
});

export default i18n;
